import { i18n } from "@src/i18n/i18n";
import { SourceInfo } from "../entities/source-info";
import { UserInfo } from "../entities/user-info";
import store from "@src/reducers/store";

export class ZendeskContactUrlBuilder {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  buildContactUrl(info: UserInfo): string {
    const language = store.getState().yourSlice.language;

    // Fetch translations via i18n
    const texts = {
      supportSubject: i18n(
        "modules.zendeskContactUrl.supportSubject",
        language,
      ),
      greeting: i18n("modules.zendeskContactUrl.greeting", language),
      bodyIntro: i18n("modules.zendeskContactUrl.bodyIntro", language),
      userInfoHeader: i18n(
        "modules.zendeskContactUrl.userInfoHeader",
        language,
      ),
      emailLabel: i18n("modules.zendeskContactUrl.emailLabel", language),
      namespaceLabel: i18n(
        "modules.zendeskContactUrl.namespaceLabel",
        language,
      ),
      firstNameLabel: i18n(
        "modules.zendeskContactUrl.firstNameLabel",
        language,
      ),
      lastNameLabel: i18n("modules.zendeskContactUrl.lastNameLabel", language),
      describeIssue: i18n("modules.zendeskContactUrl.describeIssue", language),
    };

    const body = encodeURIComponent(
      `${texts.greeting}

${texts.bodyIntro}

${texts.describeIssue}

---

${texts.userInfoHeader}:
${texts.emailLabel}: ${info.email}
${texts.namespaceLabel}: ${info.namespace}
${texts.firstNameLabel}: ${info.givenName}
${texts.lastNameLabel}: ${info.familyName}

---
`,
    );

    return `mailto:support@tomorrowthings.com?subject=${encodeURIComponent(
      texts.supportSubject,
    )}&body=${body}`;
  }

  buildContactUrlForFailedGatewayOnboarding(
    userInfo: UserInfo,
    sourceDsn: string,
    connectionId?: string,
  ): string {
    const language = store.getState().yourSlice.language;

    const texts = {
      onboardingSupportSubject: i18n(
        "modules.zendeskContactUrl.onboardingSupportSubject",
        language,
      ),
      greeting: i18n("modules.zendeskContactUrl.greeting", language),
      failedGatewayOnboardingDescription: i18n(
        "modules.zendeskContactUrl.failedGatewayOnboardingDescription",
        language,
      ),
      connectionIdDescription: i18n(
        "modules.zendeskContactUrl.connectionIdDescription",
        language,
      ),
      userInfoHeader: i18n(
        "modules.zendeskContactUrl.userInfoHeader",
        language,
      ),
      emailLabel: i18n("modules.zendeskContactUrl.emailLabel", language),
      namespaceLabel: i18n(
        "modules.zendeskContactUrl.namespaceLabel",
        language,
      ),
      firstNameLabel: i18n(
        "modules.zendeskContactUrl.firstNameLabel",
        language,
      ),
      lastNameLabel: i18n("modules.zendeskContactUrl.lastNameLabel", language),
      sourceDsnLabel: i18n(
        "modules.zendeskContactUrl.sourceDsnLabel",
        language,
      ),
      connectionIdLabel: i18n(
        "modules.zendeskContactUrl.connectionIdLabel",
        language,
      ),
      describeIssue: i18n("modules.zendeskContactUrl.describeIssue", language),
    };

    const lines: string[] = [
      texts.greeting,
      "",
      texts.failedGatewayOnboardingDescription,
      "",
      `${texts.sourceDsnLabel}: ${sourceDsn}`,
    ];

    if (connectionId) {
      lines.push(
        "",
        texts.connectionIdDescription,
        `${texts.connectionIdLabel}: ${connectionId}`,
      );
    }

    lines.push(
      "",
      "---",
      "",
      `${texts.userInfoHeader}:`,
      `${texts.emailLabel}: ${userInfo.email}`,
      `${texts.namespaceLabel}: ${userInfo.namespace}`,
      `${texts.firstNameLabel}: ${userInfo.givenName}`,
      `${texts.lastNameLabel}: ${userInfo.familyName}`,
      "",
      "---",
      "",
    );

    const body = encodeURIComponent(lines.join("\n"));

    return `mailto:support@tomorrowthings.com?subject=${encodeURIComponent(
      texts.onboardingSupportSubject,
    )}&body=${body}`;
  }

  buildContactUrlForFailedSourceConnection(
    userInfo: UserInfo,
    sourceDsn: string,
    gatewayUuid: string,
    connectionId?: string,
  ): string {
    const language = store.getState().yourSlice.language;

    const texts = {
      onboardingSupportSubject: i18n(
        "modules.zendeskContactUrl.onboardingSupportSubject",
        language,
      ),
      greeting: i18n("modules.zendeskContactUrl.greeting", language),
      failedSourceConnectionDescription: i18n(
        "modules.zendeskContactUrl.failedSourceConnectionDescription",
        language,
      ),
      connectionIdDescription: i18n(
        "modules.zendeskContactUrl.connectionIdDescription",
        language,
      ),
      userInfoHeader: i18n(
        "modules.zendeskContactUrl.userInfoHeader",
        language,
      ),
      emailLabel: i18n("modules.zendeskContactUrl.emailLabel", language),
      namespaceLabel: i18n(
        "modules.zendeskContactUrl.namespaceLabel",
        language,
      ),
      firstNameLabel: i18n(
        "modules.zendeskContactUrl.firstNameLabel",
        language,
      ),
      lastNameLabel: i18n("modules.zendeskContactUrl.lastNameLabel", language),
      sourceDsnLabel: i18n(
        "modules.zendeskContactUrl.sourceDsnLabel",
        language,
      ),
      connectionIdLabel: i18n(
        "modules.zendeskContactUrl.connectionIdLabel",
        language,
      ),
      describeIssue: i18n("modules.zendeskContactUrl.describeIssue", language),
    };

    const lines: string[] = [
      texts.greeting,
      "",
      `${texts.failedSourceConnectionDescription} ${gatewayUuid}`,
      "",
      `${texts.sourceDsnLabel}: ${sourceDsn}`,
    ];

    if (connectionId) {
      lines.push(
        "",
        texts.connectionIdDescription,
        `${texts.connectionIdLabel}: ${connectionId}`,
      );
    }

    lines.push(
      "",
      "---",
      "",
      `${texts.userInfoHeader}:`,
      `${texts.emailLabel}: ${userInfo.email}`,
      `${texts.namespaceLabel}: ${userInfo.namespace}`,
      `${texts.firstNameLabel}: ${userInfo.givenName}`,
      `${texts.lastNameLabel}: ${userInfo.familyName}`,
      "",
      "---",
      "",
    );

    const body = encodeURIComponent(lines.join("\n"));

    return `mailto:support@tomorrowthings.com?subject=${encodeURIComponent(
      texts.onboardingSupportSubject,
    )}&body=${body}`;
  }

  buildBlueprintHelpRequestUrl(
    userInfo: UserInfo,
    sourceInfo: SourceInfo,
  ): string {
    const language = store.getState().yourSlice.language;

    // Fetch all translations via i18n
    const texts = {
      blueprintSupportSubject: i18n(
        "modules.zendeskContactUrl.blueprintSupportSubject",
        language,
      ),
      greeting: i18n("modules.zendeskContactUrl.greeting", language),
      blueprintBodyIntro: i18n(
        "modules.zendeskContactUrl.blueprintBodyIntro",
        language,
      ),
      userInfoHeader: i18n(
        "modules.zendeskContactUrl.userInfoHeader",
        language,
      ),
      emailLabel: i18n("modules.zendeskContactUrl.emailLabel", language),
      namespaceLabel: i18n(
        "modules.zendeskContactUrl.namespaceLabel",
        language,
      ),
      firstNameLabel: i18n(
        "modules.zendeskContactUrl.firstNameLabel",
        language,
      ),
      lastNameLabel: i18n("modules.zendeskContactUrl.lastNameLabel", language),
      machineManufacturerLabel: i18n(
        "modules.zendeskContactUrl.machineManufacturerLabel",
        language,
      ),
      machineTypeLabel: i18n(
        "modules.zendeskContactUrl.machineTypeLabel",
        language,
      ),
      machineAmountLabel: i18n(
        "modules.zendeskContactUrl.machineAmountLabel",
        language,
      ),
      platformMachineNameLabel: i18n(
        "modules.zendeskContactUrl.platformMachineNameLabel",
        language,
      ),
      sourceDsnLabel: i18n(
        "modules.zendeskContactUrl.sourceDsnLabel",
        language,
      ),
      placeholder: i18n("modules.zendeskContactUrl.placeholder", language),
      doNotChange: i18n("modules.zendeskContactUrl.doNotChange", language),
    };

    const body = encodeURIComponent(
      `${texts.greeting}

${texts.blueprintBodyIntro}

- ${texts.machineManufacturerLabel}: ${texts.placeholder}
- ${texts.machineTypeLabel}: ${texts.placeholder}
- ${texts.machineAmountLabel}: ${texts.placeholder}

${texts.platformMachineNameLabel}: ${sourceInfo.name} ${texts.doNotChange}
${texts.sourceDsnLabel}: ${sourceInfo.dsn} ${texts.doNotChange}

---

${texts.userInfoHeader}:
${texts.emailLabel}: ${userInfo.email}
${texts.namespaceLabel}: ${userInfo.namespace}
${texts.firstNameLabel}: ${userInfo.givenName}
${texts.lastNameLabel}: ${userInfo.familyName}

---
`,
    );

    return `mailto:support@tomorrowthings.com?subject=${encodeURIComponent(
      texts.blueprintSupportSubject,
    )}&body=${body}`;
  }
}
